// BookingPageForm.js - Create/Edit Booking Page Form

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, CircularProgress, Alert,
  Stack, IconButton, TextField, Switch, FormControlLabel, Divider,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  EventAvailable as BookingIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { bookingPageService } from './activityCommService';

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' }
];

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Australia/Sydney',
  'Pacific/Auckland'
];

const defaultAvailability = {
  mon: { enabled: true, start: '09:00', end: '17:00' },
  tue: { enabled: true, start: '09:00', end: '17:00' },
  wed: { enabled: true, start: '09:00', end: '17:00' },
  thu: { enabled: true, start: '09:00', end: '17:00' },
  fri: { enabled: true, start: '09:00', end: '17:00' },
  sat: { enabled: false, start: '09:00', end: '17:00' },
  sun: { enabled: false, start: '09:00', end: '17:00' }
};

const BookingPageForm = () => {
  const { chatbotId, pageId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = !!pageId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [canModify, setCanModify] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    slug: '',
    duration: 30,
    buffer_before: 0,
    buffer_after: 0,
    min_notice: 24,
    max_days_ahead: 60,
    timezone: 'UTC',
    confirmation_message: 'Your booking has been confirmed! We look forward to meeting with you.',
    primary_color: '#1976d2',
    custom_questions: []
  });

  const [availability, setAvailability] = useState(defaultAvailability);

  useEffect(() => {
    document.title = isEditing
      ? 'Edit Booking Page | Activity & Communications'
      : 'Create Booking Page | Activity & Communications';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (isEditing && canModify) {
      loadPage();
    }
  }, [canModify, pageId]);

  const checkPermissions = async () => {
    try {
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanModify(modifyPerm);
      if (!modifyPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadPage = async () => {
    setLoading(true);
    try {
      const res = await bookingPageService.getById(pageId);
      const page = res.data || res;

      setForm({
        title: page.title || '',
        description: page.description || '',
        slug: page.slug || '',
        duration: page.duration || 30,
        buffer_before: page.buffer_before || 0,
        buffer_after: page.buffer_after || 0,
        min_notice: page.min_notice || 24,
        max_days_ahead: page.max_days_ahead || 60,
        timezone: page.timezone || 'UTC',
        confirmation_message: page.confirmation_message || '',
        primary_color: page.primary_color || '#1976d2',
        custom_questions: page.custom_questions || []
      });

      // Parse available_hours
      if (page.available_hours) {
        const parsed = typeof page.available_hours === 'string'
          ? JSON.parse(page.available_hours)
          : page.available_hours;

        const newAvailability = { ...defaultAvailability };
        Object.keys(parsed).forEach(day => {
          if (newAvailability[day] !== undefined && Array.isArray(parsed[day]) && parsed[day].length > 0) {
            newAvailability[day] = {
              enabled: true,
              start: parsed[day][0].start || '09:00',
              end: parsed[day][0].end || '17:00'
            };
          }
        });
        // Mark days not in the parsed object as disabled
        DAYS.forEach(({ key }) => {
          if (!parsed[key] || (Array.isArray(parsed[key]) && parsed[key].length === 0)) {
            newAvailability[key] = { ...newAvailability[key], enabled: false };
          }
        });
        setAvailability(newAvailability);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load booking page', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAvailabilityChange = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const addCustomQuestion = () => {
    setForm(prev => ({
      ...prev,
      custom_questions: [...prev.custom_questions, { label: '', type: 'text', required: false }]
    }));
  };

  const updateCustomQuestion = (index, field, value) => {
    setForm(prev => {
      const questions = [...prev.custom_questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, custom_questions: questions };
    });
  };

  const removeCustomQuestion = (index) => {
    setForm(prev => ({
      ...prev,
      custom_questions: prev.custom_questions.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!form.title) {
      enqueueSnackbar('Title is required', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      // Build available_hours JSON
      const available_hours = {};
      DAYS.forEach(({ key }) => {
        if (availability[key].enabled) {
          available_hours[key] = [{ start: availability[key].start, end: availability[key].end }];
        }
      });

      const payload = {
        ...form,
        available_hours,
        duration: parseInt(form.duration, 10),
        buffer_before: parseInt(form.buffer_before, 10),
        buffer_after: parseInt(form.buffer_after, 10),
        min_notice: parseInt(form.min_notice, 10),
        max_days_ahead: parseInt(form.max_days_ahead, 10)
      };

      if (isEditing) {
        await bookingPageService.update(pageId, payload);
        enqueueSnackbar('Booking page updated successfully', { variant: 'success' });
      } else {
        await bookingPageService.create(payload);
        enqueueSnackbar('Booking page created successfully', { variant: 'success' });
      }

      navigate(`/chatbot/${chatbotId}/crm/activity/booking-pages`);
    } catch (err) {
      enqueueSnackbar('Failed to save booking page', { variant: 'error' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canModify) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to create or edit booking pages.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 }, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/booking-pages`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <BookingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {isEditing ? 'Edit Booking Page' : 'Create Booking Page'}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/booking-pages`)}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Box>

      {/* Basic Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Basic Info</Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          <TextField
            label="Title"
            fullWidth
            required
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
          />
          <TextField
            label="Slug (URL-friendly identifier)"
            fullWidth
            value={form.slug}
            onChange={(e) => handleChange('slug', e.target.value)}
            helperText="Used in the public booking URL"
          />
        </Stack>
      </Paper>

      {/* Duration & Buffers */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Duration & Buffers</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Duration (minutes)"
              type="number"
              fullWidth
              value={form.duration}
              onChange={(e) => handleChange('duration', e.target.value)}
              inputProps={{ min: 5 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Buffer Before (minutes)"
              type="number"
              fullWidth
              value={form.buffer_before}
              onChange={(e) => handleChange('buffer_before', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Buffer After (minutes)"
              type="number"
              fullWidth
              value={form.buffer_after}
              onChange={(e) => handleChange('buffer_after', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Scheduling Rules */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Scheduling Rules</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Minimum Notice (hours)"
              type="number"
              fullWidth
              value={form.min_notice}
              onChange={(e) => handleChange('min_notice', e.target.value)}
              inputProps={{ min: 0 }}
              helperText="How far in advance someone must book"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Max Days Ahead"
              type="number"
              fullWidth
              value={form.max_days_ahead}
              onChange={(e) => handleChange('max_days_ahead', e.target.value)}
              inputProps={{ min: 1 }}
              helperText="How far into the future bookings are allowed"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Availability */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Availability</Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1}>
          {DAYS.map(({ key, label }) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: 1,
                bgcolor: availability[key].enabled ? 'transparent' : 'grey.50'
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={availability[key].enabled}
                    onChange={(e) => handleAvailabilityChange(key, 'enabled', e.target.checked)}
                  />
                }
                label={label}
                sx={{ width: 160 }}
              />
              {availability[key].enabled && (
                <>
                  <TextField
                    type="time"
                    size="small"
                    label="Start"
                    value={availability[key].start}
                    onChange={(e) => handleAvailabilityChange(key, 'start', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <Typography variant="body2" color="text.secondary">to</Typography>
                  <TextField
                    type="time"
                    size="small"
                    label="End"
                    value={availability[key].end}
                    onChange={(e) => handleAvailabilityChange(key, 'end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                </>
              )}
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Timezone */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Timezone</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControl fullWidth>
          <InputLabel>Timezone</InputLabel>
          <Select
            value={form.timezone}
            label="Timezone"
            onChange={(e) => handleChange('timezone', e.target.value)}
          >
            {TIMEZONES.map(tz => (
              <MenuItem key={tz} value={tz}>{tz}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Confirmation */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Confirmation</Typography>
        <Divider sx={{ mb: 2 }} />
        <TextField
          label="Confirmation Message"
          fullWidth
          multiline
          rows={3}
          value={form.confirmation_message}
          onChange={(e) => handleChange('confirmation_message', e.target.value)}
          helperText="Message shown after a successful booking"
        />
      </Paper>

      {/* Branding */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Branding</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Primary Color"
            type="color"
            value={form.primary_color}
            onChange={(e) => handleChange('primary_color', e.target.value)}
            sx={{ width: 120 }}
            InputLabelProps={{ shrink: true }}
          />
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: form.primary_color,
              border: '1px solid',
              borderColor: 'grey.300'
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {form.primary_color}
          </Typography>
        </Box>
      </Paper>

      {/* Custom Questions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Custom Questions</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addCustomQuestion}>
            Add Question
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {form.custom_questions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No custom questions. Add questions to collect additional information from bookers.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {form.custom_questions.map((q, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Question Label"
                  fullWidth
                  size="small"
                  value={q.label}
                  onChange={(e) => updateCustomQuestion(idx, 'label', e.target.value)}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={q.type || 'text'}
                    label="Type"
                    onChange={(e) => updateCustomQuestion(idx, 'type', e.target.value)}
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="textarea">Textarea</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={q.required || false}
                      onChange={(e) => updateCustomQuestion(idx, 'required', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Req"
                  sx={{ minWidth: 80 }}
                />
                <IconButton color="error" onClick={() => removeCustomQuestion(idx)} size="small">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Bottom Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/booking-pages`)}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default BookingPageForm;
